import { AlertTypeEnum, type Alert } from '@/api/alert/types/alert';
import { updateUserFcmToken } from '@/api/user/user.api';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
  type Token,
} from '@capacitor/push-notifications';
import { toastController } from '@ionic/vue';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { usePushAlertClick } from './use-alert-click';
import { useReadAlerts } from '@/api/alert/mutations/use-read-alerts';
import { QueryKey } from '@/api/common/types/query-key.enum';
import { type AnalyticsParams, logEvent } from '@/services/analytics';
import { queueOrNavigatePushAlert } from './pending-push-navigation';
import { parsePushAlert, shouldMarkPushAlertRead } from './alert-navigation';

export const ANDROID_DEFAULT_NOTIFICATION_CHANNEL = {
  id: 'default',
  name: 'Notifications',
  importance: 4 as const,
  vibration: true,
};

type AndroidNotificationChannelDeps = {
  getPlatform: () => string;
  createChannel: typeof PushNotifications.createChannel;
};

export const ensureAndroidDefaultNotificationChannel = async (
  deps: AndroidNotificationChannelDeps = {
    getPlatform: () => Capacitor.getPlatform(),
    createChannel: channel => PushNotifications.createChannel(channel),
  }
): Promise<void> => {
  if (deps.getPlatform() !== 'android') return;
  try {
    await deps.createChannel(ANDROID_DEFAULT_NOTIFICATION_CHANNEL);
  } catch {
    // Creating the channel must not block token registration.
  }
};

export const usePushPermissionState = () => useQuery({ queryKey: ['push'], queryFn: checkPermissions });

let pushListenersRegistered = false;

export const handlePushNotificationActionPerformed = (
  notification: Pick<PushNotificationSchema, 'data'>,
  onAlertClick: (alert: Alert) => void,
  readAlerts: (alertIds: string[]) => void,
  queryClient: QueryClient
): void => {
  const alert = parsePushAlert(notification.data?.data);
  if (!alert) return;

  const params: AnalyticsParams = { alert_type: alert.type };
  if (alert.data.workoutEventId) {
    params.workout_id = alert.data.workoutEventId;
  }
  void logEvent('push_notification', params);
  if (alert.type === AlertTypeEnum.chat) {
    // The pushed message exists only on the server: the socket is disconnected while
    // backgrounded and nothing else on the tap path refetches the chat list.
    void queryClient.invalidateQueries({ queryKey: [QueryKey.GetChats], refetchType: 'all' });
  }
  if (shouldMarkPushAlertRead(alert)) {
    readAlerts([alert.id]);
  }
  queueOrNavigatePushAlert(alert, onAlertClick);
};

export const initPushNotificationListeners = (
  onAlertClick: (alert: Alert) => void,
  readAlerts: (alertIds: string[]) => void,
  queryClient: QueryClient
) => {
  if (!Capacitor.isNativePlatform() || pushListenersRegistered) return;
  pushListenersRegistered = true;

  PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
    const alert = parsePushAlert(notification.data?.data);
    if (alert) {
      queryClient.setQueryData([QueryKey.GetAlerts], (oldData: Alert[] | undefined) => {
        if (!oldData?.length) return [alert];
        if (oldData.some(a => a.id === alert.id)) return oldData;
        return [...oldData, alert];
      });
      const toast = await toastController.create({
        message: alert.title,
        duration: 1000,
        position: 'top',
        color: 'dark',
        cssClass: 'text-center',
      });
      await toast.present();
    }
  });

  PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }: ActionPerformed) => {
    handlePushNotificationActionPerformed(notification, onAlertClick, readAlerts, queryClient);
  });
};

export const useEnablePushNotifications = () => {
  const queryClient = useQueryClient();
  const onAlertClick = usePushAlertClick();
  const { mutate: readAlerts } = useReadAlerts();
  return useMutation({
    mutationFn: async () => {
      if (!Capacitor.isNativePlatform()) return;
      try {
        initPushNotificationListeners(onAlertClick, readAlerts, queryClient);
        const token = (await registerPushToken()) ?? (await getStoredFcmToken());
        if (token) {
          await updateUserFcmToken(token);
          await storeFcmToken(token);
          queryClient.invalidateQueries({ queryKey: ['push'] });
        }
        return true;
      } catch (_error) {
        return false;
      } finally {
        setLastPushPermissionPrompt();
      }
    },
  });
};
const checkPermissions = async () => {
  if (Capacitor.isNativePlatform()) {
    const result = await PushNotifications.checkPermissions();
    return result.receive;
  }
  return 'granted';
};

export const registerPushToken = async (): Promise<string | null> => {
  const result = await PushNotifications.requestPermissions();
  if (result.receive !== 'granted') {
    throw new Error('Push registration not granted');
  }

  await ensureAndroidDefaultNotificationChannel();

  return new Promise<string | null>(resolve => {
    const timeout = setTimeout(() => resolve(null), 5000);

    void PushNotifications.addListener('registration', async (token: Token) => {
      clearTimeout(timeout);
      await storeFcmToken(token.value);
      resolve(token.value);
    });

    void PushNotifications.addListener('registrationError', () => {
      clearTimeout(timeout);
      resolve(null);
    });

    PushNotifications.register();
  });
};

export const shouldRequestPushPermission = async () => {
  const lastPrompt = await getLastPushPermissionPrompt();
  if (!lastPrompt) return true;

  const lastPromptDate = new Date(lastPrompt);
  const now = new Date();
  const diff = now.getTime() - lastPromptDate.getTime();
  const diffInDays = diff / (1000 * 3600 * 24);

  return diffInDays > 2;
};

export const setLastPushPermissionPrompt = async () => {
  await Preferences.set({
    key: 'lastPushPermissionPrompt',
    value: new Date().toISOString(),
  });
};

const getLastPushPermissionPrompt = async () => {
  const lastPrompt = await Preferences.get({ key: 'lastPushPermissionPrompt' });
  return lastPrompt.value;
};

const storeFcmToken = async (token: string) => {
  await Preferences.set({ key: 'fcmToken', value: token });
};

const getStoredFcmToken = async () => {
  const stored = await Preferences.get({ key: 'fcmToken' });
  return stored.value ?? null;
};
